export const MAX_STEPS = 12;

/**
 * Target RTP for OPTIMAL play. The ladder is calibrated so that an
 * optimal-timing player's RTP lands here at every cash-out step; weaker timing
 * yields a lower (never higher) RTP, so the house is never at risk. Set at the
 * middle of the 97-100% band so per-step survival noise can't push realized
 * RTP above 100% (house-negative).
 */
export const TARGET_RTP = 0.985;

/**
 * Per-step survival probability the ladder is calibrated against.
 *
 * KEY INSIGHT: survival is a property of the physics + jump timing, NOT of the
 * payout table — changing multipliers does not change how often a player
 * catches the next vine. So the ladder is calibrated ONCE against a measured
 * survival curve and is then exact.
 *
 * These values come from 100k headless sessions of an optimal-timing player
 * through the authoritative engine, on real provably-fair seeds (2026-05-22):
 * steps 1-14 are measured directly; steps 15-24 use the stable ~0.72 plateau
 * (deep steps are reached too rarely to estimate per-step). The first jump is
 * structurally the hardest (~0.59) because the initial vine starts under-swung.
 * See tools/sim/results/RTP_REPORT.md for the full derivation.
 *
 * The previous model assumed a flat 0.88 success rate (=> 1.10x at step 1),
 * which the physics never delivered, collapsing realized RTP to ~61-65%.
 */
const STEP_SURVIVAL = [
  0.5939, 0.6709, 0.6986, 0.7150, 0.7190, 0.7201, 0.7181, 0.7063, 0.7136,
  0.7069, 0.7174, 0.7093,
  // Steps 13-24 (unused while MAX_STEPS=12) hold the ~0.71 plateau so the
  // ladder stays well-defined if the cap is ever raised.
  0.710, 0.710, 0.710, 0.710, 0.710, 0.710, 0.710, 0.710, 0.710, 0.710,
  0.710, 0.710,
];

/**
 * Cash-out multiplier after surviving `step` rungs.
 *
 *   multiplier(n) = floor( TARGET_RTP / prod(survival_1..n) * 100 ) / 100
 *
 * Then RTP(n) = P(reach n) * multiplier(n) ≈ TARGET_RTP for optimal play.
 * Floored to 2 decimals (always rounds the payout DOWN, so RTP never exceeds
 * the target).
 */
export function multiplierForStep(step: number): number {
  if (step <= 0) return 1.0;
  const n = Math.min(step, MAX_STEPS);
  let cumSurvival = 1;
  for (let i = 0; i < n; i++) cumSurvival *= STEP_SURVIVAL[i];
  return Math.floor((TARGET_RTP / cumSurvival) * 100) / 100;
}
